/*
 * presentationLeadTime — floor the Confirmed Presentation Date picker on the
 * new-tender form at Date of Submission + N business days, N by Submission
 * Type (MOHO-162).
 *
 * The {type: days} map arrives on data-lead-days, emitted from config/mh.php
 * by the form-page template. The floor lands on the picker as the native
 * `min`, so the browser greys those days out and blocks submit with its own
 * message; App\Rules\PresentationLeadTime is the authoritative server half
 * and reads the same map. Forms without the three fields are left alone.
 *
 * Controls are found by name within the form. The select primitive posts
 * through a hidden input and dispatches a bubbling native `change` on it
 * (select-field.js); its no-JS fallback shares the name and is disabled by
 * nojsFallback, but init order isn't guaranteed, so prefer whichever enabled
 * copy holds a value — the same lookup conditionalField uses.
 */
export default function presentationLeadTime() {
    return {
        form: null,
        target: null,
        onFormChange: null,

        init() {
            this.form = this.$el.querySelector('form');
            this.target = this.form?.querySelector('input[name="confirmed_presentation_date"]');
            if (!this.target) return;

            this.onFormChange = () => this.apply();
            this.form.addEventListener('change', this.onFormChange);
            this.form.addEventListener('input', this.onFormChange);
            this.apply();
        },

        destroy() {
            if (this.form && this.onFormChange) {
                this.form.removeEventListener('change', this.onFormChange);
                this.form.removeEventListener('input', this.onFormChange);
            }
        },

        apply() {
            const min = this.earliest();
            if (min) {
                this.target.min = min;
            } else {
                this.target.removeAttribute('min');
            }
        },

        earliest() {
            const days = this.leadDays()[this.value('submission_type')];
            const submitted = this.value('date_of_submission');
            if (!days || !/^\d{4}-\d{2}-\d{2}$/.test(submitted)) return '';

            // Local-date arithmetic throughout: toISOString() would shift the
            // day across midnight UTC for anyone east of Greenwich.
            const [y, m, d] = submitted.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            for (let remaining = days; remaining > 0; ) {
                date.setDate(date.getDate() + 1);
                const day = date.getDay();
                if (day !== 0 && day !== 6) remaining--;
            }

            const pad = (n) => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
        },

        leadDays() {
            try {
                return JSON.parse(this.$el.dataset.leadDays || '{}');
            } catch {
                return {};
            }
        },

        value(name) {
            const controls = Array.from(
                this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`),
            ).filter((control) => !control.disabled);

            return controls.find((control) => control.value !== '')?.value ?? '';
        },
    };
}
